import React from 'react'
import { connect } from 'react-redux'
import {
  addQuestionAnimatedGIF, addChoiceAnimatedGIF, uploadQuestionAnimatedGIF, uploadChoiceAnimatedGIF,
} from '../../prosemirror/utils/editorActions/addAnimatedGIF'
import EditorInsertGIFModal from '../../components/richEditor/editorUI/modals/EditorInsertGIFModal'
import { gifSearch, fetchGifById } from '../../actions/imageSearch'
import { showNotification } from '../../actions/notifications'
import analytics from '../../utils/cdpAnalytics'

const MAX_FILE_SIZE = 20000000

class EditorInsertGIFModalContainer extends React.Component {
  constructor() {
    super()
    this.handleSelectImage = this.handleSelectImage.bind(this)
    this.handleNewImageSearch = this.handleNewImageSearch.bind(this)
    this.handleLoadMore = this.handleLoadMore.bind(this)
    this.handleSelectFile = this.handleSelectFile.bind(this)
    this.clearSearch = this.clearSearch.bind(this)
    this.insertGifFromId = this.insertGifFromId.bind(this)

    this.state = ({
      submitLoading: false,
      images: [],
      searchTerm: '',
      isSearching: false,
      noResults: false,
      searchResultCount: null,
      count: 30,
      offset: 0,
    })
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown)
    analytics.page('Insert GIF Modal')
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
  }

  handleKeyDown(e) {
    e.stopPropagation()
  }

  clearSearch() {
    this.setState({
      isSearching: false, images: [], searchTerm: '', offset: 0, searchResultCount: null, noResults: false,
    })
  }

  insertGifFromId(id) { // fetch gif from giphy id and insert it
    this.setState({ isSearching: true, noResults: false, submitLoading: true })
    this.props.fetchGifById(id).then((response) => {
      const {
        gifyID,
        title,
        attribution,
        giphyOriginalStill,
        giphyOriginalGifURL,
        giphyOriginalWebPURL,
        giphySlug,
        giphyUser,
        width,
        height,
      } = response
      this.props.closeInsertImageModal()
      const {
        insertPos, isQuestion, questionPos, questionNode,
      } = this.props
      if (isQuestion) {
        this.props.addQuestionAnimatedGIF(
          gifyID,
          giphyOriginalStill,
          giphyOriginalGifURL,
          giphyOriginalWebPURL,
          giphySlug,
          giphyUser,
          attribution,
          title,
          width,
          height,
          insertPos,
          questionPos,
          questionNode,
        )
      } else {
        this.props.addChoiceAnimatedGIF(
          gifyID,
          giphyOriginalStill,
          giphyOriginalGifURL,
          giphyOriginalWebPURL,
          giphySlug,
          giphyUser,
          attribution,
          title,
          width,
          height,
          insertPos,
        )
      }
    })
  }

  async handleNewImageSearch(searchTerm) {
    const { count } = this.state
    const { gifSearch } = this.props
    if (searchTerm) {
      analytics.track('GIF search', {
        searchTerm,
      })
      this.setState({ isSearching: true, noResults: false, images: [] })
      try {
        const {
          searchResults,
          resultCount,
          totalCount,
          nextOffset,
        } = await gifSearch(searchTerm, count, 0)

        if (resultCount === 0) {
          this.setState({ noResults: true })
        }

        this.setState({
          isSearching: false,
          images: searchResults,
          searchTerm,
          offset: nextOffset,
          searchResultCount: totalCount,
        })
      } catch (error) {
        this.setState({
          isSearching: false, images: [], searchTerm, noResults: true,
        })
      }
    }
  }

  async handleLoadMore() {
    const { searchTerm, count, offset } = this.state
    const { gifSearch } = this.props
    this.setState({ isSearching: true })
    try {
      const {
        searchResults,
        nextOffset,
        totalCount,
      } = await gifSearch(searchTerm, count, offset)
      const { images } = this.state

      this.setState({
        isSearching: false,
        images: [...images, ...searchResults],
        offset: nextOffset,
        searchResultCount: totalCount,
      })
    } catch (error) {
      this.setState({ isSearching: false })
    }

    analytics.track('GIF search load more', {
      searchTerm,
    })
  }

  handleSelectImage(imageIndex) {
    if (!this.state.submitLoading) {
      this.setState({ submitLoading: true })
      const img = this.state.images[imageIndex]
      const {
        gifyID, giphyOriginalStill, giphyOriginalGifURL, giphySlug, giphyUser, giphyOriginalWebPURL, attribution, title, width, height,
      } = img
      this.props.closeInsertImageModal()
      const {
        insertPos, isQuestion, questionPos, questionNode,
      } = this.props
      if (isQuestion) {
        this.props.addQuestionAnimatedGIF(gifyID, giphyOriginalStill, giphyOriginalGifURL, giphyOriginalWebPURL, giphySlug, giphyUser, attribution, title, width, height, insertPos, questionPos, questionNode)
      } else {
        this.props.addChoiceAnimatedGIF(gifyID, giphyOriginalStill, giphyOriginalGifURL, giphyOriginalWebPURL, giphySlug, giphyUser, attribution, title, width, height, insertPos)
      }
    }
  }

  handleSelectFile(e) {
    const fileList = e.target.files
    const file = fileList[0]
    if (file.size > MAX_FILE_SIZE) {
      this.props.closeInsertImageModal()
      this.props.showNotification('The maximum file size you can upload is 20mb.', 'Sadly your GIF file is too large. ', 'destroy')
    } else {
      const {
        insertPos, isQuestion, questionPos, questionNode,
      } = this.props
      if (isQuestion) {
        this.props.uploadQuestionAnimatedGIF(insertPos, questionPos, questionNode, file)
      } else {
        this.props.uploadChoiceAnimatedGIF(insertPos, file)
      }
      this.props.closeInsertImageModal()
    }
  }

  render() {
    return (
      <div className='editor-insertImageModalContainer'>
        <EditorInsertGIFModal
          noResults={this.state.noResults}
          isSearching={this.state.isSearching}
          handleNewImageSearch={this.handleNewImageSearch}
          loadMore={this.handleLoadMore}
          searchTerm={this.state.searchTerm}
          onSelectImage={this.handleSelectImage}
          images={this.state.images}
          handleSelectFile={this.handleSelectFile}
          closeInsertImageModal={this.props.closeInsertImageModal}
          searchResultCount={this.state.searchResultCount}
          clearSearch={this.clearSearch}
          insertGifFromId={this.insertGifFromId}
        />
        <div onClick={this.props.closeInsertImageModal} className='editor-insertImageModalContainerBG' />
      </div>
    )
  }
}

function mapStateToProps() {
  return {}
}

export default connect(
  mapStateToProps,
  {
    addQuestionAnimatedGIF,
    addChoiceAnimatedGIF,
    gifSearch,
    uploadQuestionAnimatedGIF,
    uploadChoiceAnimatedGIF,
    fetchGifById,
    showNotification,
  },
)(EditorInsertGIFModalContainer)
